import { FicheContainer, useFiche } from '@mairistem/fiche';
import type { ErrorField } from '@mairistem/fiche/lib/ts/src/types';
import { isNil } from 'lodash';
import React from 'react';
import ProjetFicheConsumer from '@/src/components/ProjetFiche/ProjetFicheConsumer';
import { ProjetStatus } from '@/src/constants/projetStatus';
import type { Projet } from '@/src/interfaces/projet';
import ProjetFicheBarConsumer from './ProjetFicheBarConsumer';

const handleUrl = (method: string, data) => {
	const { identifiant } = data;
	const params =
		'withComposants=true&withEtiquettes=true&withConditionsVersements=true&withSubventions=true&withUtilisateursProjet=true&withService=true';

	switch (method) {
		case 'get':
			return `/api/finances/subventionsPercues/projets/${identifiant}?${params}`;
		case 'put':
		case 'delete':
			return `/api/finances/subventionsPercues/projets/${identifiant}`;
		default:
			return '/api/finances/subventionsPercues/projets';
	}
};

const ProjetFiche = () => {
	const [projetService] = useFiche(handleUrl);

	const handleApiError = (errors): ErrorField[] => {
		if (errors?.status >= 500) {
			return [
				{
					id: null,
					message: 'Une erreur technique est survenue',
				},
			];
		}

		return errors?.response?.data.map((error) => ({
			id: error?.id || null,
			message: error?.message,
		}));
	};

	const handleValidate = (data): ErrorField[] => {
		const { libelle = null, composants, status } = data;

		const errors = [];
		if (isNil(libelle) || libelle.length === 0) {
			errors.push({
				id: 'subventionsPercues-libelleProjet',
				message: 'Le libellé du projet est obligatoire',
			});
		}

		if (!isNil(composants?.find((c) => isNil(c.libelle)))) {
			errors.push({
				id: 'subventionsPercues.composants.libelle',
				message: 'Le libellé des composants est obligatoire',
			});
		}

		if (!status) {
			errors.push({
				id: 'subventionsPercues.status',
				message: 'Le statut du projet est obligatoire',
			});
		}

		return errors;
	};

	const handleCreatingFicheProjet = (data): Projet => ({
		...data,
		status: ProjetStatus.CONCEPTION,
	});

	return (
		<FicheContainer
			type="projet"
			service={projetService}
			onValidate={handleValidate}
			onApiError={handleApiError}
			hasHistory={false}
			onCreating={handleCreatingFicheProjet}
		>
			{({ identifiant, ...handlers }) => (
				<>
					<ProjetFicheBarConsumer identifiant={identifiant} {...handlers} />
					<ProjetFicheConsumer identifiant={identifiant} {...handlers} />
				</>
			)}
		</FicheContainer>
	);
};

export default ProjetFiche;
